import * as CL from '@design-system/component-library';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { fetchNumberRange } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import { cancelMsg, confirmMsg, somethingWentWrongMsg, subscriptionMsg, t } from '../../common/i18n/index.js';
import { findBestMatchForExtensionNumber } from '../../common/utils/pbxUtils.js';
import { forceInternationalPhoneNumberFormat, formatPhoneNumber } from '../../common/utils/phoneNumberUtils.js';
import { getOpeningFeeAddons } from '../../common/utils/ringUtils.js';
import { getSubscriptionUrlId } from '../../common/utils/subscriptionUtils.js';
import { NumberRange } from '../../generated/api/numberRange.js';
import { SubscriptionPbxConfiguration } from '../../generated/api/subscriptionPbxConfiguration.js';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails.js';
import { SubscriptionType } from '../../generated/api/subscriptionType.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { AttachPbxContentConfirmationDialogContent } from '../AttachPbxContentConfirmationDialogContent/AttachPbxContentConfirmationDialogContent.js';
import { Loading } from '../Loading/index.js';
import { ConfirmationModal } from '../Modal/ConfirmationModal.js';
import { ExtensionNumberAndRangeContent } from './ExtensionNumber.js';
import {
  getCurrentPbxSolutionNumberRanges,
  getRelatedExtensionNumberRangeId,
  getSelectedPbxSolution,
  isPbxConfigComplete,
  sortNumberRangesInPbxSolutions,
} from './attachPbxUtils.js';
import { CorporateNumberRangeSelect } from './components/CorporateNumberRangeSelect.js';
import { CorporateNumberSelect } from './components/CorporateNumberSelect.js';
import { PbxSolutionSelect } from './components/PbxSolutionSelect.js';
import { ServiceLevelFields } from './components/ServiceLevelFields.js';
import { constructDeliveryAddress, createOfferFromSelectedAddons, getAccountId } from './prepareOrder.js';
import type { NumberRangeData } from '../../common/loaders.js';
import type { ConfiguredOffer } from '../../common/types/commercialProduct.js';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { DeliveryAddress } from '../../generated/api/deliveryAddress.js';
import type { OnlineModel } from '../../generated/api/onlineModel.js';
import type { Subscription } from '../../generated/api/subscription.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { ServiceLevel, ServiceLevelAddon } from '../AttachRing/ServiceLevelAndAddonsInterfaces.js';

import './AttachPbxContent.scss';

const EMPTY_NUMBER_RANGES: NumberRangeData[] = [];

// Exported for use by onlineOrderEpicUtils (ConfiguredCommercialProduct type)
export type RingSettingsConfiguration = SubscriptionPbxConfiguration & {
  pbxServiceLevels: ServiceLevel[];
  pbxServiceLevelAddons: ServiceLevelAddon[];
  selectedExtensionNumber: string;
};

export interface AttachPbxContentProps {
  category: CategoryKey;
  companyInfo?: CompanyInfoResponse;
  numberRanges?: NumberRangeData[];
  subscription?: Subscription;
  pbxServiceLevels?: ServiceLevel[];
  pbxServiceLevelAddons?: ServiceLevelAddon[];
  onSubmitRingOrder?: (
    offer: ConfiguredOffer,
    deliveryAddress: DeliveryAddress,
    billingAccountId: string
  ) => Promise<void>;
  associations?: AssociationRecord[];
  dependencies?: DependencyRecord[];
  ringModels?: OnlineModel[];
  addOnAssociations: AssociationRecord[];
  onClickSubscription: (id: string) => void;
}

export interface AttachPbxContentFormValues {
  pbxSolutionId: string;
  pbxServiceLevels: ServiceLevel[];
  pbxServiceLevelAddons: ServiceLevelAddon[];
  corporateNumberRangeId?: string;
  corporateNumber?: string;
  extensionNumber?: string;
  extensionRangeId?: string;
}

// This function gets a possible "related" extension number which is the last 3 digits of corporate number. This extension
// number is returned in case it is found in the number range
const getRelatedExtensionNumber = (extensionRange: NumberRangeData | undefined, selectedCorporateNumber: string) => {
  if (!extensionRange?.displayedNumbers) {
    return undefined;
  }
  const availableExtensionNumbers = extensionRange.displayedNumbers;
  const relatedExtensionNumber = findBestMatchForExtensionNumber(selectedCorporateNumber, availableExtensionNumbers);

  if (Number(relatedExtensionNumber) < 700 || Number(relatedExtensionNumber) >= 900) {
    return relatedExtensionNumber;
  }
  return undefined;
};

const getSelectedServiceLevel = (serviceLevels: ServiceLevel[]): ServiceLevel =>
  serviceLevels.find(serviceLevel => serviceLevel.selected) || ({} as ServiceLevel);

const createCompletePbxConfiguration = (
  configuration: AttachPbxContentFormValues,
  subscription?: Subscription
): SubscriptionPbxConfiguration => ({
  ...configuration,
  busyAction: SubscriptionPbxConfiguration.BusyActionEnum.NEXT_NUMBER_IN_CHAIN,
  corporateNumber: forceInternationalPhoneNumberFormat(configuration.corporateNumber)!,
  delayForChainCalls: 20,
  pbxConfigurationDetails: { currentActiveChain: SubscriptionPbxDetails.CurrentActiveChainEnum.WORKING_HOURS },
  subscriptionDisplayId: subscription?.subscriptionDisplayId,
  pbxSolutionId: configuration.pbxSolutionId,
  workingHoursReachabilityChain: subscription?.details?.mobile?.mobileNumber
    ? [subscription.details.mobile.mobileNumber]
    : undefined,
});

const getAddonsForSelectedServiceLevel = (
  serviceLevels: ServiceLevel[],
  serviceLevelAddons: ServiceLevelAddon[],
  onlySelected: boolean
): ServiceLevelAddon[] => {
  const selectedServiceLevel = getSelectedServiceLevel(serviceLevels);
  return serviceLevelAddons.filter(addon =>
    onlySelected
      ? addon.serviceLevelId === selectedServiceLevel.id && addon.selected
      : addon.serviceLevelId === selectedServiceLevel.id
  );
};

// Gets a default extension number to use. First we try to get the "related" (see getRelatedExtensionNumber for more details)
// extension number if it exists. Otherwise we return the first number in the list.
const getDefaultExtensionNumber = (extensionRange: NumberRangeData | undefined, defaultCorporateNumber: string) => {
  const defaultExtensionNumber = getRelatedExtensionNumber(extensionRange, defaultCorporateNumber);
  if (defaultExtensionNumber !== undefined) {
    return defaultExtensionNumber;
  }
  const displayedNumbers = extensionRange?.displayedNumbers;
  if (!displayedNumbers) {
    return undefined;
  }

  return displayedNumbers.find(number => Number(number) < 700 || Number(number) >= 900);
};

const getPbxSolutions = (pbxSolutions: Subscription[]) => {
  const filteredSolutions = pbxSolutions.filter(
    pbxSolution => pbxSolution.subscriptionType === SubscriptionType.MOBILE_PBX
  );
  return filteredSolutions.length > 0 ? filteredSolutions : pbxSolutions;
};

const getExtensionNumberRanges = (pbxSolutions: Subscription[], selectedPbxSolutionId?: string) => {
  return pbxSolutions
    .find(sub => sub.subscriptionId === selectedPbxSolutionId)!
    .details!.mobilePbx!.numberRanges.filter(
      numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.EXTENSION
    )
    .filter(numberRange => Number(numberRange.startNumber) < 700 || Number(numberRange.startNumber) >= 900);
};

const getDefaultCorporateNumber = (corporateRange?: NumberRangeData) => {
  return corporateRange?.displayedNumbers?.[0];
};

export const AttachRingPbxContent = ({
  companyInfo,
  subscription,
  numberRanges = EMPTY_NUMBER_RANGES,
  onClickSubscription,
  pbxServiceLevels,
  pbxServiceLevelAddons,
  associations,
  dependencies,
  ringModels,
  onSubmitRingOrder,
  addOnAssociations,
}: AttachPbxContentProps) => {
  const dispatch = useDispatch();
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localRanges, setLocalRanges] = useState<NumberRangeData[]>(numberRanges);

  // Memoize to keep a stable reference — this object is used as a dependency in
  // multiple useEffects and would otherwise trigger them on every render.
  // Note that this will be removed as part of this ticket, in later PR's.
  const rangeMap = useMemo(() => Object.fromEntries(localRanges.map(r => [r.rangeId, r])), [localRanges]);

  const isLoading = !pbxServiceLevels?.length || !pbxServiceLevelAddons?.length;
  const pbxSolutions = getPbxSolutions(companyInfo?.pbxSolutions ?? []);
  const defaultPbxSolutionId = sortNumberRangesInPbxSolutions(pbxSolutions)[0].subscriptionId;
  const defaultPbxSolution = getSelectedPbxSolution(pbxSolutions, defaultPbxSolutionId);
  const defaultCorporateRanges = defaultPbxSolution.details!.mobilePbx!.numberRanges.filter(
    numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.PSTN
  );
  const defaultCorporateRange = defaultCorporateRanges[0].rangeId;

  const methods = useForm<AttachPbxContentFormValues>({
    defaultValues: {
      corporateNumberRangeId: defaultCorporateRange,
      pbxSolutionId: defaultPbxSolutionId,
      pbxServiceLevelAddons,
      pbxServiceLevels,
    },
  });

  const formValue: AttachPbxContentFormValues = methods.watch();

  useEffect(() => {
    const defaultCorporateNumber = getDefaultCorporateNumber(
      rangeMap[formValue.corporateNumberRangeId ?? defaultCorporateRange]
    );

    if (!formValue.corporateNumber && defaultCorporateNumber) {
      methods.setValue('corporateNumber', defaultCorporateNumber);
    }
  }, [defaultCorporateRange, formValue.corporateNumberRangeId, formValue.corporateNumber, methods, rangeMap]);

  useEffect(() => {
    const loadMissingRange = async (rangeId?: string) => {
      if (rangeId && !rangeMap[rangeId]) {
        try {
          const data = await fetchNumberRange(rangeId, mdmId);
          setLocalRanges(prev => (prev.some(r => r.rangeId === rangeId) ? prev : [...prev, data]));
        } catch {
          addNotification(t.JDTO(somethingWentWrongMsg), 'error');
        }
      }
    };
    loadMissingRange(formValue.corporateNumberRangeId);
    loadMissingRange(formValue.extensionRangeId);
  }, [formValue.corporateNumberRangeId, formValue.extensionRangeId, rangeMap, mdmId, dispatch]);

  useEffect(() => {
    if (formValue.pbxSolutionId && formValue.corporateNumberRangeId === undefined) {
      methods.setValue(
        'corporateNumberRangeId',
        getCurrentPbxSolutionNumberRanges(
          pbxSolutions.find(pbxSolution => pbxSolution.subscriptionId === formValue.pbxSolutionId)
        )[0].rangeId
      );
    }
  }, [formValue.corporateNumberRangeId, formValue.pbxSolutionId, methods, pbxSolutions]);

  useEffect(() => {
    const rangeId = formValue.corporateNumberRangeId ?? defaultCorporateRanges[0]?.rangeId;
    const defaultCorporateNumber = rangeMap[rangeId]?.displayedNumbers?.[0];
    if (formValue.extensionRangeId === undefined && defaultCorporateNumber !== undefined) {
      const relatedExtensionNumberRangeId = getRelatedExtensionNumberRangeId(
        getSelectedPbxSolution(pbxSolutions, formValue.pbxSolutionId),
        defaultCorporateNumber
      );

      if (relatedExtensionNumberRangeId) {
        methods.setValue('extensionRangeId', relatedExtensionNumberRangeId);
      } else {
        const defaultExtensionRange = getSelectedPbxSolution(
          pbxSolutions,
          formValue.pbxSolutionId
        ).details!.mobilePbx!.numberRanges.find(
          numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.EXTENSION
        );
        if (defaultExtensionRange) {
          methods.setValue('extensionRangeId', defaultExtensionRange.rangeId);
        }
      }
    }
  }, [
    defaultCorporateRanges,
    formValue.corporateNumberRangeId,
    formValue.extensionRangeId,
    formValue.pbxSolutionId,
    methods,
    rangeMap,
    pbxSolutions,
  ]);

  useEffect(() => {
    if (!formValue.extensionNumber && formValue.extensionRangeId && formValue.corporateNumber) {
      const defaultExtensionNumber = getDefaultExtensionNumber(
        rangeMap[formValue.extensionRangeId],
        formValue.corporateNumber
      );

      if (defaultExtensionNumber) {
        methods.setValue('extensionNumber', defaultExtensionNumber);
      }
    }
  }, [formValue.corporateNumber, formValue.extensionNumber, formValue.extensionRangeId, methods, rangeMap]);

  if (isLoading) {
    return <Loading />;
  }

  const pbxConfig = createCompletePbxConfiguration(formValue, subscription);

  const onDisplayMoreNumbers = () => {
    const rangeId = formValue.corporateNumberRangeId ?? '';
    const range = rangeMap[rangeId];
    if (!range?.allNumbers) {
      return;
    }
    const newDisplayed = range.allNumbers.slice(0, (range.displayedNumbers?.length ?? 0) + 4);
    setLocalRanges(prev => prev.map(r => (r.rangeId === rangeId ? { ...r, displayedNumbers: newDisplayed } : r)));
  };

  const onRetryLoadRange = async (rangeId: string) => {
    try {
      const reloaded = await fetchNumberRange(rangeId, mdmId);
      setLocalRanges(prev => {
        const exists = prev.some(r => r.rangeId === rangeId);
        return exists ? prev.map(r => (r.rangeId === rangeId ? reloaded : r)) : [...prev, reloaded];
      });
    } catch {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
    }
  };

  const onRingConfirm = async () => {
    if (!subscription) {
      return;
    }
    setIsSaving(true);
    const selectedServiceLevel = getSelectedServiceLevel(formValue.pbxServiceLevels);
    const selectedServiceLevelAddons = getAddonsForSelectedServiceLevel(
      formValue.pbxServiceLevels,
      formValue.pbxServiceLevelAddons,
      true
    );
    const mobilePbxServiceLevelDetails = {
      mobileVoiceSubscriptionId: subscription.subscriptionId ?? '',
      mobilePbxSubscriptionId: pbxConfig.pbxSolutionId ?? '',
      corporateNumber: pbxConfig.corporateNumber ?? '',
      extensionNumber: pbxConfig.extensionNumber ?? '',
      corporateNumberRangeId: pbxConfig.corporateNumberRangeId ?? '',
      extensionRangeId: pbxConfig.extensionRangeId ?? '',
    };
    const offer = createOfferFromSelectedAddons(
      selectedServiceLevel,
      selectedServiceLevelAddons,
      mobilePbxServiceLevelDetails
    );
    const deliveryAddress = constructDeliveryAddress(companyInfo, subscription);
    const accountId = getAccountId(subscription);

    if (onSubmitRingOrder) {
      try {
        await onSubmitRingOrder(offer, deliveryAddress, accountId);
      } catch {
        addNotification(t.JDTO(somethingWentWrongMsg), 'error');
      }
    }
    setIsSaving(false);
    setShowConfirmationModal(false);
  };

  const onSubmit = (values: AttachPbxContentFormValues) => {
    if (!subscription) {
      return false;
    }
    const config = createCompletePbxConfiguration(values, subscription);
    if (!isPbxConfigComplete(config)) {
      return false;
    }
    setShowConfirmationModal(true);
    return true;
  };

  return (
    <div className="of-attach-pbx-content">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <h3>{t.RHLU('Ring settings of subscription')}</h3>
          <p>{t.BLQP(`Elisa Ring is an easy way of making mobile phones part of a company's telephone system.`)}</p>
          <CL.Description
            items={[
              {
                title: t.P674(subscriptionMsg),
                description: formatPhoneNumber(subscription?.details?.mobile?.mobileNumber || ''),
              },
            ]}
          />
          <PbxSolutionSelect
            pbxSolutions={pbxSolutions}
            isRing={true}
            onSelect={(value: string) => {
              methods.setValue('corporateNumberRangeId', undefined);
              methods.setValue('corporateNumber', undefined);
              methods.setValue('extensionNumber', undefined);
              methods.setValue('extensionRangeId', undefined);
              methods.setValue('pbxSolutionId', value);
            }}
            selectedValue={formValue.pbxSolutionId}
          />
          <ServiceLevelFields
            serviceLevels={pbxServiceLevels}
            addons={pbxServiceLevelAddons}
            associations={associations}
            dependencies={dependencies}
            ringModels={ringModels}
            onChange={(field: keyof AttachPbxContentFormValues, value: ServiceLevel[] | ServiceLevelAddon[]) =>
              methods.setValue(field, value)
            }
          />
          <CorporateNumberRangeSelect
            selectedValue={formValue.corporateNumberRangeId}
            onSelect={(value: string) => {
              methods.setValue('corporateNumberRangeId', value);
              methods.setValue('corporateNumber', undefined);
              methods.setValue('extensionNumber', undefined);
            }}
            currentPbxSolutionNumberRanges={getCurrentPbxSolutionNumberRanges(
              pbxSolutions.find(pbxSolution => pbxSolution.subscriptionId === formValue.pbxSolutionId)
            )}
          />
          <CorporateNumberSelect
            onSelect={(field: keyof AttachPbxContentFormValues, value: string) => {
              methods.setValue(field, value);
              methods.setValue('extensionNumber', undefined);
            }}
            selectedValue={formValue.corporateNumber}
            addOnAssociations={addOnAssociations}
            rangeNumbers={rangeMap[formValue.corporateNumberRangeId ?? '']}
            onDisplayMoreNumbers={onDisplayMoreNumbers}
            retryAction={() => onRetryLoadRange(formValue.corporateNumberRangeId ?? '')}
          />
          <ExtensionNumberAndRangeContent
            extensionNumberRanges={getExtensionNumberRanges(pbxSolutions, formValue.pbxSolutionId)}
            selectedExtensionNumber={formValue.extensionNumber}
            selectedExtensionRangeId={formValue.extensionRangeId}
            selectedCorporateNumber={formValue.corporateNumber}
            rangeMap={rangeMap}
            onRetryLoadRange={onRetryLoadRange}
            updateExtensionRangeId={value => methods.setValue('extensionRangeId', value)}
            updateExtensionNumber={value => methods.setValue('extensionNumber', value)}
          />
          <div className="of-barrings-content__actions">
            <CL.Button disabled={!isPbxConfigComplete(formValue)} size="l" color="primary" type="submit">
              {t.QVYK(confirmMsg)}
            </CL.Button>
            {subscription && (
              <CL.Button size="l" color="link" onClick={() => onClickSubscription(getSubscriptionUrlId(subscription))}>
                {t.B2V1(cancelMsg)}
              </CL.Button>
            )}
          </div>
        </form>
      </FormProvider>
      {showConfirmationModal && subscription && (
        <ConfirmationModal
          heading={t.ZYIU('Elisa Ring solution for subscription')}
          onCancel={() => setShowConfirmationModal(false)}
          onConfirm={onRingConfirm}
          isLoading={isSaving}
        >
          <AttachPbxContentConfirmationDialogContent
            numberCategory={rangeMap[pbxConfig.corporateNumberRangeId ?? '']?.numberCategory}
            pbxConfig={pbxConfig}
            voiceSub={subscription}
            selectedServiceLevelAddons={getAddonsForSelectedServiceLevel(
              formValue.pbxServiceLevels,
              formValue.pbxServiceLevelAddons,
              true
            )}
            selectedServiceLevel={getSelectedServiceLevel(formValue.pbxServiceLevels)}
            subscriptionServiceLevelAddons={getAddonsForSelectedServiceLevel(
              formValue.pbxServiceLevels,
              formValue.pbxServiceLevelAddons,
              false
            )}
            openingFeeAddons={getOpeningFeeAddons(
              getAddonsForSelectedServiceLevel(formValue.pbxServiceLevels, formValue.pbxServiceLevelAddons, true),
              dependencies,
              associations
            )}
            addOnAssociations={addOnAssociations}
          />
        </ConfirmationModal>
      )}
    </div>
  );
};
